import { AppShell, Group, Text, Badge, Avatar, Menu, UnstyledButton, Box, Tooltip, Center, useMantineColorScheme, SegmentedControl } from '@mantine/core'
import {
  IconChevronDown,
  IconLogout,
  IconSun,
  IconMoon,
  IconDeviceDesktop,
} from '@tabler/icons-react'
import { Outlet, useNavigate } from 'react-router'
import { useAuthStore } from '../../stores/authStore'

function getEnvironmentBadge(): { label: string; color: string } | null {
  const apiUrl = import.meta.env.VITE_API_URL || ''
  if (!apiUrl) return { label: 'DEV', color: 'green' }
  if (apiUrl.includes('staging')) return { label: 'Staging', color: 'orange' }
  return null
}

function LauncherHeader() {
  const { user, logout } = useAuthStore()
  const { colorScheme, setColorScheme } = useMantineColorScheme()
  const envBadge = getEnvironmentBadge()
  const navigate = useNavigate()

  const handleLogout = async () => {
    await logout()
    navigate('/', { replace: true })
  }

  const displayName = user?.profile.displayName || user?.email || 'User'
  const initials = displayName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  return (
    <Group h="100%" px="md" justify="space-between">
      <Group gap="sm">
        <Text size="lg" fw={600}>
          Passport - PaperAirplane Platform
        </Text>
        {envBadge && (
          <Badge variant="filled" color={envBadge.color} size="sm">
            {envBadge.label}
          </Badge>
        )}
      </Group>

      <Menu position="bottom-end" withArrow shadow="md" width={220}>
        <Menu.Target>
          <UnstyledButton>
            <Group gap="xs">
              <Avatar size="sm" radius="xl" color="blue">
                {initials}
              </Avatar>
              <Box visibleFrom="sm">
                <Text size="sm" fw={500} lh={1}>
                  {displayName}
                </Text>
                {user?.email && displayName !== user.email && (
                  <Text size="xs" c="dimmed" lh={1.2}>
                    {user.email}
                  </Text>
                )}
              </Box>
              <IconChevronDown size={14} />
            </Group>
          </UnstyledButton>
        </Menu.Target>

        <Menu.Dropdown>
          <Menu.Label>Account</Menu.Label>
          <Box px="xs" pb="xs">
            <Text size="sm" fw={500}>{displayName}</Text>
            <Text size="xs" c="dimmed">{user?.email}</Text>
          </Box>

          <Menu.Divider />

          <Menu.Label>Appearance</Menu.Label>
          <Box px="xs" pb="xs">
            <SegmentedControl
              fullWidth
              size="xs"
              value={colorScheme}
              onChange={(value) => setColorScheme(value as 'light' | 'dark' | 'auto')}
              data={[
                {
                  value: 'light',
                  label: (
                    <Tooltip label="Light mode" position="bottom" withArrow>
                      <Center><IconSun size={14} /></Center>
                    </Tooltip>
                  ),
                },
                {
                  value: 'dark',
                  label: (
                    <Tooltip label="Dark mode" position="bottom" withArrow>
                      <Center><IconMoon size={14} /></Center>
                    </Tooltip>
                  ),
                },
                {
                  value: 'auto',
                  label: (
                    <Tooltip label="System default" position="bottom" withArrow>
                      <Center><IconDeviceDesktop size={14} /></Center>
                    </Tooltip>
                  ),
                },
              ]}
            />
          </Box>

          <Menu.Divider />

          <Menu.Item
            color="red"
            leftSection={<IconLogout size={14} />}
            onClick={handleLogout}
          >
            Sign out
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Group>
  )
}

export function LauncherLayout() {
  return (
    <AppShell header={{ height: 50 }} padding="xl">
      <AppShell.Header>
        <LauncherHeader />
      </AppShell.Header>
      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  )
}
